import { useState, useRef, useEffect, useCallback, Fragment } from 'react'
import { Bot, Send, X, Bug, MessageSquare, Lightbulb, TrendingUp, Plug, ThumbsUp, ThumbsDown, Check } from 'lucide-react'
import { streamChat, recordSessionFeedback } from '../../lib/chatApi'
import { useChatState } from '../../hooks/useChatState'
import type { FeedbackTypeOption } from './WidgetFeedbackType'

type FeedbackChoice = 'solved' | 'unresolved'

const SLACK_URL = 'https://exante.slack.com/archives/C0ACURB6A9X'
const FEEDBACK_REGEX = /\[FEEDBACK:(bug|feature|improvement|integration)(?:\|([^|]*)\|([^\]]*))?\]/

const FEEDBACK_BUTTONS: Record<FeedbackTypeOption, { icon: React.ElementType; label: string; color: string; hoverColor: string }> = {
  bug: { icon: Bug, label: 'Report a Bug', color: 'bg-destructive/10 text-destructive', hoverColor: 'hover:bg-destructive/20' },
  feature: { icon: Lightbulb, label: 'Request Feature', color: 'bg-blue-500/10 text-blue-600 dark:text-blue-400', hoverColor: 'hover:bg-blue-500/20' },
  improvement: { icon: TrendingUp, label: 'Suggest Improvement', color: 'bg-green-500/10 text-green-600 dark:text-green-400', hoverColor: 'hover:bg-green-500/20' },
  integration: { icon: Plug, label: 'Request Integration', color: 'bg-purple-500/10 text-purple-600 dark:text-purple-400', hoverColor: 'hover:bg-purple-500/20' },
}

export function WidgetChat({ isWidgetOpen }: { isWidgetOpen: boolean }) {
  const { state, setMessages, setSessionId } = useChatState()
  const messages = state.messages

  const [input, setInput] = useState('')
  const [isStreaming, setIsStreaming] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Per-message feedback choice (keyed by message index). Local to the
  // open widget; server-side resolution is updated on every click.
  const [feedbackChoice, setFeedbackChoice] = useState<Record<number, FeedbackChoice>>({})

  const abortRef = useRef<AbortController | null>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const handleFeedback = useCallback((messageIdx: number, choice: FeedbackChoice) => {
    setFeedbackChoice(prev => ({ ...prev, [messageIdx]: choice }))
    if (!state.sessionId) return
    recordSessionFeedback(state.sessionId, choice).catch((err) => {
      setFeedbackChoice(prev => {
        const next = { ...prev }
        delete next[messageIdx]
        return next
      })
      setError(err instanceof Error ? err.message : 'Could not record your feedback')
    })
  }, [state.sessionId])

  // Auto-scroll on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  // Cancel streaming when widget closes
  useEffect(() => {
    if (!isWidgetOpen) {
      abortRef.current?.abort()
    }
  }, [isWidgetOpen])

  const handleSend = useCallback(() => {
    if (!input.trim() || isStreaming) return

    const userMessage = { role: 'user' as const, content: input.trim() }
    const allMessages = [...messages, userMessage]

    setMessages([...allMessages, { role: 'assistant', content: '' }])
    setInput('')
    setError(null)
    setIsStreaming(true)

    const controller = new AbortController()
    abortRef.current = controller

    const chatPayload = allMessages.map(({ role, content }) => ({ role, content }))

    streamChat(
      chatPayload,
      (chunk) => {
        setMessages((prev: typeof messages) => {
          const updated = [...prev]
          const last = updated[updated.length - 1]
          updated[updated.length - 1] = { ...last, content: last.content + chunk }
          return updated
        })
      },
      (returnedSessionId) => {
        if (returnedSessionId) setSessionId(returnedSessionId)
        setIsStreaming(false)
        inputRef.current?.focus()
      },
      (msg) => {
        setIsStreaming(false)
        setError(msg)
        inputRef.current?.focus()
      },
      controller.signal,
      state.sessionId,
    )
  }, [input, isStreaming, messages, state.sessionId, setMessages, setSessionId])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* Messages area */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
        {messages.length === 0 && (
          <div className="flex-1 flex items-center justify-center h-full">
            <p className="text-sm text-muted-foreground text-center px-8">
              Ask me anything about the CC Platform — apps, workspaces, deployments, and more.
            </p>
          </div>
        )}

        {messages.map((msg, i) =>
          msg.role === 'user' ? (
            <div key={i} className="flex justify-end">
              <div className="max-w-[80%] bg-primary text-primary-foreground rounded-2xl rounded-br-sm px-3.5 py-2.5 text-sm">
                <span className="whitespace-pre-wrap">{msg.content}</span>
              </div>
            </div>
          ) : (
            <div key={i} className="flex justify-start gap-2">
              <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-1">
                <Bot className="w-3.5 h-3.5 text-primary" />
              </div>
              <div className="max-w-[80%] bg-secondary/50 rounded-2xl rounded-bl-sm px-3.5 py-2.5 text-sm text-foreground">
                {msg.content ? (
                  <>
                    <div className="whitespace-pre-wrap">{renderContent(stripMarkers(msg.content))}</div>
                    {(() => {
                      const aiFeedback = parseFeedback(msg.content)
                      if (isStreaming && i === messages.length - 1) return null

                      // AI self-escalated via [FEEDBACK:...] marker
                      if (aiFeedback) {
                        const btn = FEEDBACK_BUTTONS[aiFeedback.type]
                        const BtnIcon = btn.icon
                        return (
                          <div className="flex gap-2 mt-3 pt-2 border-t border-border/50">
                            <a
                              href={SLACK_URL}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary/10 text-primary text-xs font-medium hover:bg-primary/20 transition-colors"
                            >
                              <MessageSquare className="w-3.5 h-3.5" />
                              Ask in #team-claude
                            </a>
                            <button
                              onClick={() => {
                                window.dispatchEvent(new CustomEvent('help-widget:feedback', {
                                  detail: {
                                    feedbackType: aiFeedback.type,
                                    title: aiFeedback.title,
                                    description: aiFeedback.description,
                                  },
                                }))
                              }}
                              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg ${btn.color} text-xs font-medium ${btn.hoverColor} transition-colors`}
                            >
                              <BtnIcon className="w-3.5 h-3.5" />
                              {btn.label}
                            </button>
                          </div>
                        )
                      }

                      // User feedback: "Did this help?"
                      const choice = feedbackChoice[i]
                      if (choice === 'solved') {
                        return (
                          <div className="flex items-center gap-1.5 mt-3 pt-2 border-t border-border/50 text-xs text-green-600 dark:text-green-400">
                            <Check className="w-3.5 h-3.5" />
                            Glad this helped.
                          </div>
                        )
                      }
                      if (choice === 'unresolved') {
                        return (
                          <div className="mt-3 pt-2 border-t border-border/50 space-y-2">
                            <p className="text-xs text-muted-foreground">
                              Sorry I couldn&apos;t help. Try one of these:
                            </p>
                            <div className="flex gap-2 flex-wrap">
                              <a
                                href={SLACK_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary/10 text-primary text-xs font-medium hover:bg-primary/20 transition-colors"
                              >
                                <MessageSquare className="w-3.5 h-3.5" />
                                Ask in #team-claude
                              </a>
                              <button
                                onClick={() => {
                                  window.dispatchEvent(new CustomEvent('help-widget:feedback', {
                                    detail: { feedbackType: 'bug', title: '', description: '' },
                                  }))
                                }}
                                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-secondary text-foreground text-xs font-medium hover:bg-secondary/80 transition-colors"
                              >
                                <Bug className="w-3.5 h-3.5" />
                                Submit feedback
                              </button>
                            </div>
                          </div>
                        )
                      }
                      // No feedback yet — show Yes/No
                      return (
                        <div className="flex items-center gap-2 mt-3 pt-2 border-t border-border/50">
                          <span className="text-xs text-muted-foreground">Did this help?</span>
                          <button
                            onClick={() => handleFeedback(i, 'solved')}
                            className="flex items-center gap-1 px-2 py-1 rounded-md hover:bg-green-500/10 text-muted-foreground hover:text-green-600 dark:hover:text-green-400 text-xs transition-colors"
                            title="Yes, problem solved"
                          >
                            <ThumbsUp className="w-3.5 h-3.5" />
                            Yes
                          </button>
                          <button
                            onClick={() => handleFeedback(i, 'unresolved')}
                            className="flex items-center gap-1 px-2 py-1 rounded-md hover:bg-destructive/10 text-muted-foreground hover:text-destructive text-xs transition-colors"
                            title="No, still need help"
                          >
                            <ThumbsDown className="w-3.5 h-3.5" />
                            No
                          </button>
                        </div>
                      )
                    })()}
                  </>
                ) : isStreaming && i === messages.length - 1 ? (
                  <div className="flex gap-1 py-1">
                    <div
                      className="w-1.5 h-1.5 rounded-full bg-muted-foreground/50 animate-bounce"
                      style={{ animationDelay: '0ms' }}
                    />
                    <div
                      className="w-1.5 h-1.5 rounded-full bg-muted-foreground/50 animate-bounce"
                      style={{ animationDelay: '150ms' }}
                    />
                    <div
                      className="w-1.5 h-1.5 rounded-full bg-muted-foreground/50 animate-bounce"
                      style={{ animationDelay: '300ms' }}
                    />
                  </div>
                ) : null}
              </div>
            </div>
          )
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Error message */}
      {error && (
        <div className="px-4 pb-2 flex items-center gap-2">
          <span className="text-xs text-destructive flex-1">{error}</span>
          <button
            onClick={() => setError(null)}
            className="text-muted-foreground hover:text-foreground"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      )}

      {/* Input area */}
      <div className="flex-shrink-0 border-t border-border p-3">
        <div className="flex items-center gap-2">
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask a question..."
            className="flex-1 bg-secondary/50 border border-border rounded-lg px-3 py-2.5 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
          <button
            onClick={handleSend}
            disabled={!input.trim() || isStreaming}
            className="ml-0 w-9 h-9 rounded-lg bg-primary text-primary-foreground flex items-center justify-center hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
        <p className="text-[11px] text-muted-foreground text-center pt-1.5">
          Can&apos;t find what you need? Ask in{' '}
          <a
            href={SLACK_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:underline"
          >
            #team-claude
          </a>
        </p>
      </div>
    </div>
  )
}

/** Strip all feedback markers from displayed text. */
function stripMarkers(text: string): string {
  return text.replace(FEEDBACK_REGEX, '').trimEnd()
}

interface ParsedFeedback {
  type: FeedbackTypeOption
  title: string
  description: string
}

/** Extract feedback type, title, and description from a marker in the message. */
function parseFeedback(text: string): ParsedFeedback | null {
  const match = text.match(FEEDBACK_REGEX)
  if (!match) return null
  return {
    type: match[1] as FeedbackTypeOption,
    title: (match[2] || '').trim(),
    description: (match[3] || '').trim(),
  }
}

/**
 * Simple markdown renderer for chat messages.
 * Handles: **bold**, *italic*, `code`, [links](url), and bullet lists.
 */
function renderContent(text: string): React.ReactNode {
  const lines = text.split('\n')

  return lines.map((line, lineIdx) => {
    // Bullet list items
    const bulletMatch = line.match(/^(\s*)[•\-*]\s+(.+)/)
    if (bulletMatch) {
      return (
        <div key={lineIdx} className="flex gap-1.5 ml-1">
          <span className="text-muted-foreground">•</span>
          <span>{renderInline(bulletMatch[2])}</span>
        </div>
      )
    }

    // Numbered list items
    const numMatch = line.match(/^(\s*)\d+[.)]\s+(.+)/)
    if (numMatch) {
      const num = line.match(/^(\s*)(\d+)/)
      return (
        <div key={lineIdx} className="flex gap-1.5 ml-1">
          <span className="text-muted-foreground min-w-[1rem] text-right">{num?.[2]}.</span>
          <span>{renderInline(numMatch[2])}</span>
        </div>
      )
    }

    // Empty line = paragraph break
    if (!line.trim()) {
      return <div key={lineIdx} className="h-2" />
    }

    // Regular text line
    return <Fragment key={lineIdx}>{lineIdx > 0 && lines[lineIdx - 1]?.trim() ? <br /> : null}{renderInline(line)}</Fragment>
  })
}

/** Render inline markdown: **bold**, *italic*, `code`, [link](url) */
function renderInline(text: string): React.ReactNode {
  const parts = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`|\[[^\]]+\]\([^)]+\))/)
  if (parts.length === 1) return text

  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i} className="font-semibold">{part.slice(2, -2)}</strong>
    }
    if (part.startsWith('*') && part.endsWith('*') && !part.startsWith('**')) {
      return <em key={i}>{part.slice(1, -1)}</em>
    }
    if (part.startsWith('`') && part.endsWith('`')) {
      return (
        <code key={i} className="bg-muted px-1 py-0.5 rounded text-xs font-mono">
          {part.slice(1, -1)}
        </code>
      )
    }
    const linkMatch = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/)
    if (linkMatch) {
      return (
        <a key={i} href={linkMatch[2]} target="_blank" rel="noopener noreferrer"
          className="text-primary hover:underline">
          {linkMatch[1]}
        </a>
      )
    }
    return part
  })
}
